import { graphql, readInlineData } from 'react-relay';

import {
    PricingGuidancePriceRange,
    pricingGuidanceHelpers_item$key,
    pricingGuidanceHelpers_item$data,
} from './__generated__/pricingGuidanceHelpers_item.graphql';

const priceRanges: PricingGuidancePriceRange[] = [
    'OUT_OF_RANGE_LOW',
    'LOWER_RANGE',
    'NEAR_MEDIAN_LOW',
    'MEDIAN',
    'NEAR_MEDIAN_HIGH',
    'HIGHER_RANGE',
    'OUT_OF_RANGE_HIGH',
];

const itemFragment = graphql`
    fragment pricingGuidanceHelpers_item on Item @inline {
        pricingGuidance {
            priceRange
            price {
                convertedAmountList {
                    amount
                    currency
                }
            }
        }
        displayPrice(
            isTrade: $isTrade
            buyerId: $userId
            userCountryCode: $userCountryCode
            priceBookName: $priceBookName
        ) {
            convertedAmountList {
                amount
                currency
            }
        }
    }
`;

const getDisplayPriceAmount = (
    displayPrice: pricingGuidanceHelpers_item$data['displayPrice'],
    currency: string
): number | null => {
    const itemPriceDisplay = displayPrice?.[0];
    const priceAmount = (itemPriceDisplay?.convertedAmountList || []).find(
        convertedAmount => convertedAmount && convertedAmount.currency === currency
    );

    const price = (priceAmount && priceAmount.amount) || null;

    return price;
};

export const showPricingGuidance = ({
    item: itemRef,
    currency,
}: {
    item: pricingGuidanceHelpers_item$key;
    currency: string;
}): boolean => {
    // Hide module if other display currencies are selected due to price conversion issues.
    const supportedCurrencies = ['USD', 'EUR', 'GBP'];

    if (!supportedCurrencies.includes(currency)) {
        return false;
    }

    const item = readInlineData(itemFragment, itemRef);
    const { pricingGuidance, displayPrice = null } = item;

    if (!pricingGuidance) {
        return false;
    }

    const price = getDisplayPriceAmount(displayPrice, currency);

    if (!price) {
        return false;
    }

    return true;
};

export const getGuidancePriceAmount = ({
    item: itemRef,
    currency,
}: {
    item: pricingGuidanceHelpers_item$key;
    currency: string;
}): number => {
    const item = readInlineData(itemFragment, itemRef);
    const { pricingGuidance, displayPrice } = item || {};
    const { price } = pricingGuidance || {};

    return getDisplayPriceAmount(price ? [price] : displayPrice, currency) || 0;
};

export function isPriceRangeOrHigher({
    item: itemRef,
    targetPriceRange,
}: {
    item: pricingGuidanceHelpers_item$key;
    targetPriceRange: PricingGuidancePriceRange;
}): boolean {
    const item = readInlineData(itemFragment, itemRef);
    const { priceRange } = item?.pricingGuidance || {};

    return !!priceRange && priceRanges.indexOf(priceRange) >= priceRanges.indexOf(targetPriceRange);
}
